import { create } from 'zustand';
import { SimulationEngine, type SimulationSnapshot } from '@/simulation/SimulationEngine';
import { useProjectStore } from './projectStore';

import { useUiStore } from './uiStore';
import { useLadderEditorStore } from '@/ui/editor/ladder/store/ladderEditorStore';

interface SimulationStore {
  engine: SimulationEngine;
  snapshot: SimulationSnapshot;
  speed: number;
  
  // Actions
  start: () => void;
  pause: () => void;
  stop: () => void;
  reset: () => void;
  step: () => void;
  setSpeed: (speed: number) => void;
  updateSnapshot: () => void;
  setInput: (byteOffset: number, bitOffset: number, value: boolean) => void;
}

const engine = new SimulationEngine();

export const useSimulationStore = create<SimulationStore>((set) => {
  // Snapshot polling interval when running
  let intervalId: number | null = null;
  
  const updateSnapshot = () => {
    set({ snapshot: engine.getSnapshot() });
  };
  
  const startPolling = () => {
    if (intervalId === null) {
      // ~30 FPS UI Update
      intervalId = window.setInterval(updateSnapshot, 33);
    }
  };
  
  const stopPolling = () => {
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  };

  return {
    engine,
    snapshot: engine.getSnapshot(),
    speed: 1.0,
    
    start: () => {
      const scene = useProjectStore.getState().project.scene;
      if (scene) {
        engine.process.loadDevices(scene.devices, scene.pipes || []);
      }
      engine.start();
      startPolling();
      updateSnapshot();

      useUiStore.getState().clearSelection();
      useLadderEditorStore.getState().clearSelection();
      useLadderEditorStore.getState().setActiveTool(null);
    },
    
    pause: () => {
      engine.pause();
      stopPolling();
      updateSnapshot();
    },
    
    stop: () => {
      engine.stop();
      stopPolling();
      updateSnapshot();
    },
    
    reset: () => {
      engine.reset();
      stopPolling();
      updateSnapshot();
    },
    
    step: () => {
      engine.step();
      updateSnapshot();
    },
    
    setSpeed: (speed: number) => {
      engine.simulationSpeed = speed;
      set({ speed });
    },
    
    updateSnapshot,

    setInput: (byteOffset: number, bitOffset: number, value: boolean) => {
      const { host } = engine;
      const mask = 1 << bitOffset;
      if (value) {
        if (host.physicalInputs[byteOffset] !== undefined) host.physicalInputs[byteOffset] |= mask;
      } else {
        if (host.physicalInputs[byteOffset] !== undefined) host.physicalInputs[byteOffset] &= ~mask;
      }
      // Instantly update UI if stopped or paused
      if (engine.state !== 'RUNNING') {
        updateSnapshot();
      }
    }
  };
});
